import { useAuth } from "@/modules/auth";

/**
 * Roles that represent a "seller" account.
 * Includes "seller-admin" for backward compatibility with old seller accounts.
 */
const SELLER_ROLES = ["seller", "seller-admin"] as const;

const SUPERADMIN_ROLE = "superadmin" as const;
const SALES_ROLE = "sales" as const;

/**
 * Hook for component-level role-based access control.
 * Use this to conditionally render UI elements or enable/disable actions
 * based on the authenticated user's roles.
 */
export const usePermissions = () => {
  const { userData } = useAuth();
  const roles = (userData?.roles ?? []) as string[];

  const hasRole = (role: string): boolean => roles.includes(role);

  const hasAnyRole = (requiredRoles: readonly string[]): boolean =>
    requiredRoles.some((role) => roles.includes(role));

  const isSeller = hasAnyRole(SELLER_ROLES);
  const isSuperAdmin = hasRole(SUPERADMIN_ROLE);
  const isSales = hasRole(SALES_ROLE);
  const isSellerExternal = isSeller && userData?.seller_type === "external";

  return {
    roles,
    hasRole,
    hasAnyRole,
    isSeller,
    isSuperAdmin,
    isSales,
    isSellerExternal
  };
};
